import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { useAuthorization } from '@hooks/useAuthorization';
import { useCurrentRoute } from '@hooks/useCurrentRoute';
import { useStorageFeatureFlag } from '@hooks/useStorageFeatureFlag';
import { useUser } from '@hooks/useUser';
import { FeatureFlag } from '@constants/featureFlags';
import LoadingIndicator from '@components/common/LoadingIndicator/LoadingIndicator';
import { UserRoles } from '@src/constants/enum/common';

interface PrivateRouteProps {
  component: React.ComponentType;
  roles: Array<UserRoles>;
}

const PrivateRoute = ({ component, roles, ...args }: PrivateRouteProps) => {
  const { permissions: routePermissions } = useCurrentRoute();
  const { hasPermission, isLoading } = useAuthorization();
  const { user } = useAuth0();
  const {
    user: { role: topLevelRole },
    hasSomeWorkpaceAccess,
  } = useUser();
  const isRbacEnabled = useStorageFeatureFlag(FeatureFlag.RBAC);

  // TODO(RBAC): Remove this once RBAC is enabled
  // Top level roles, should be only used by Admin
  // It might be deprecated in the future
  const hasAccess = isRbacEnabled
    ? hasPermission(routePermissions || {})
    : roles.some((role) => topLevelRole === UserRoles.ADMIN || hasSomeWorkpaceAccess(role));

  // If RBAC is enabled and permissions are still loading, show a loading state
  // This prevents premature redirects while permissions are being fetched
  if (isRbacEnabled && isLoading) {
    return <LoadingIndicator />;
  }

  if (!user || !hasAccess) return <Navigate to="/login" />;

  const AuthenticatedComponent = withAuthenticationRequired(component, args);

  return <AuthenticatedComponent />;
};

export default PrivateRoute;
